// The client TRANSPORT to the Stage-1 dashboard HTTP API. A surface FETCHES its typed projection from a GET
// read endpoint and ROUTES a write intent to a POST Gate endpoint. This is a THIN transport: it carries the
// human's intent to the owning module's Gate (via the dashboard API, which authenticates and routes) — it
// sets NO approval field, authors no content, and re-reads the projection after a write so the surface shows
// the state the OWNING module recorded (P-GATE, G-182). The authenticated Actor is resolved SERVER-SIDE by
// the dashboard API's auth middleware; the client sends NO actor/approval field in the body (mass-assignment
// banned) — it sends only the object id / message the intent needs.
//
// INJECTABLE (hermetic UI tests): every surface takes a `SurfaceApi` by prop/context. The real app supplies
// `httpSurfaceApi()` (fetch against the served endpoints); a UI test supplies a fake that returns injected
// projections and records the routed intents — no real backend network in a UI test (build constraint).

import type {
  ChatActivityItem,
  ApprovalGateKind,
  ApprovalsInboxProjection,
  ChatPlanView,
  ChatRunState,
  ChatReasoningText,
  ChatContinueResult,
  ChatSendResult,
  ChatSessionRow,
  ChatSessionTranscriptView,
  ChatTranscriptView,
  MetricEvidenceSheet,
  MetricsBoardWire,
  ScheduleHealthProjection,
  TaskBoardWire,
  TodayProjection,
} from './projections.ts';
import { streamRun } from './chat-stream.ts';
import type {
  DirectoryProjection,
  DirectoryProposalKind,
  FilesProjection,
  MemoryBrowserProjection,
  MemoryDecisionKind,
  MemoryReviewProjection,
  MemorySearchProjection,
  MessageReaderProjection,
  SettingsProjection,
  TaskDetailProjection,
} from './know-projections.ts';
import type {
  CapabilityMapProjection,
  ConnectorsProjection,
  MonitoringProjection,
  RunTranscriptProjection,
} from './operate-projections.ts';
import { httpGovernanceApi, type GovernanceClientApi } from './governance-api.ts';
import { httpKnowledgeApi } from './know-api.ts';
import { postIntent, safeNote, API_PREFIX as POST_API_PREFIX } from './post-intent.ts';
import { postTaskCancellation, type TaskCancellationRouteResult } from './task-cancellation-api.ts';
import type { TaskReviewAction, TaskReviewInput } from './task-review-api-types.ts';
import { httpApprovalApi } from './approval-api.ts';

/** The path prefix the dashboard's JSON API is served under (mirrors DASHBOARD_API_PREFIX server-side). */
const API_PREFIX = POST_API_PREFIX;

/**
 * The handlers a chat-stream subscriber supplies. The transport parses guard-cleared SSE frames and
 * dispatches them here — it fabricates nothing (P-SELF). Every branch is a BEST-EFFORT PREVIEW; the caller
 * always reconciles to `getChatTranscript` on end (the snapshot wins, streaming §F).
 */
export interface ChatStreamHandlers {
  /** An incremental guard-cleared preview fragment — APPEND to the in-progress agent turn. */
  readonly onDelta: (sequence: number, text: string) => void;
  /** A late-subscriber one-shot surfaced body — REPLACE the in-progress agent turn (never append). */
  readonly onSnapshot: (text: string) => void;
  /** The run reached a terminal RECORDED state — the caller re-reads the snapshot (which wins). */
  readonly onEnd: (state: ChatRunState) => void;
  /** The guard refused mid-stream (sanitised code only) — DISCARD the preview and self-correct. */
  readonly onRefused: (reason: string) => void;
  /** A transport error (non-OK response / socket drop) — fall back to the authoritative snapshot read. */
  readonly onError: () => void;
  /** One server-authored safe operational summary, already restricted to platform vocabulary. OPTIONAL:
   *  older subscribers ignore it. This is presentation context, never provider reasoning/chain-of-thought. */
  readonly onReasoning?: (text: ChatReasoningText) => void;
  /** One bounded, guard-cleared provider reasoning SUMMARY. It is the provider's existing surfaced summary,
   *  not raw hidden reasoning; the transport independently enforces one nonblank line of at most 160 chars. */
  readonly onReasoningSummary?: (text: string) => void;
  /** Current Agent-owned plan, already persisted and sanitised before the server emitted it. */
  readonly onPlan?: (plan: ChatPlanView) => void;
  /** One sanitised activity lifecycle item streamed live (BUILD-DEC-098). A start is inserted at its
   *  publication position; its durable terminal echo replaces that item by stable call identity. OPTIONAL
   *  (older callers ignore the event); the reconciling transcript read carries the durable trace either way. */
  readonly onActivity?: (row: ChatActivityItem) => void;
}

/** A write-intent result the surface renders honestly — routed (owning module accepted) or refused. */
export interface RouteResult {
  /** True when the owning module accepted and recorded the transition; false when the Gate refused. */
  readonly ok: boolean;
  /** A sanitised, user-facing note (never an internal error/stack — G-197); present on refusal. */
  readonly note?: string;
}

// MIRRORED_CONTRACT: `ConnectorConnectIntent.system` in
// `app/modules/dashboard/api/operate-routes.ts`. The browser bundle cannot import the server module.
export type ConnectorSystemWire = 'xero' | 'hubspot';

// MIRRORED_CONTRACT: `OrgDisplayView` in `app/modules/dashboard/api/org-display.ts`. The browser bundle
// cannot import the server module, so the two-field shape is restated here. It is the whole payload: two
// presentational strings, no config key and no adjacent setting rides along.
export interface OrgDisplayWire {
  readonly name: string;
  readonly meta: string;
}

// MIRRORED_CONTRACT: `ViewerIdentityView` in
// `app/modules/dashboard/api/viewer-identity.ts`. Display-only: no raw User id exists on this shape.
export interface ViewerIdentityWire {
  readonly name: string;
  readonly initials: string;
  readonly roleLabel: string;
}

/** The transport surface every Work component consumes — reads return projections; routes return a result. */
export interface SurfaceApi extends GovernanceClientApi {
  readonly getToday: () => Promise<TodayProjection>;
  readonly getTaskBoard: () => Promise<TaskBoardWire>;
  /** Cancel exact-owner working/blocked work at the viewed definition revision with a mandatory reason. */
  readonly cancelTask: (
    taskId: string,
    expectedRevision: number,
    reason: string,
  ) => Promise<TaskCancellationRouteResult>;
  /** Permanently delete one Task through Tasks' destructive-operation Gate. */
  readonly deleteTask: (taskId: string) => Promise<RouteResult>;
  readonly getMetricsBoard: () => Promise<MetricsBoardWire>;
  readonly getScheduleHealth: () => Promise<ScheduleHealthProjection>;
  readonly getApprovals: () => Promise<ApprovalsInboxProjection>;
  /** Fetch a Metric Value's evidence sheet on demand (value id → sheet) — audits the number (G-011). */
  readonly getMetricEvidence: (valueId: string) => Promise<MetricEvidenceSheet>;
  /** Permanently discard an unactivated proposal; the owning module refuses live/history Metrics. */
  readonly deleteMetricProposal: (metricId: string) => Promise<RouteResult>;
  /** ROUTE a collect-now intent for an ACTIVE Metric to `metrics.runImmediateCollection` (human-only; the
   *  owning module refuses the agent and an uncollectable Metric — the surface renders the honest note).
   *  The collection produces a PROPOSED Value; confirming it stays a separate Gate the human holds. */
  readonly collectMetricNow: (metricId: string) => Promise<RouteResult>;
  /**
   * ROUTE a Gate exercise to the owning module (via the dashboard API). The client sends only the object id;
   * the API authenticates the Actor and routes to the owning Gate (approveTaskStart / activateMetric /
   * approveMetricValue). The dashboard NEVER sets an approval field (P-GATE).
   */
  readonly exerciseGate: (
    kind: ApprovalGateKind,
    objectId: string,
    expectedRevision: number | null,
  ) => Promise<RouteResult>;
  /** Return a brief for changes or defer it without releasing working-state side effects. */
  readonly reviewTask: (
    taskId: string,
    action: TaskReviewAction,
    input: TaskReviewInput,
  ) => Promise<RouteResult>;
  /** The creator rebinds a Draft's owner by Team-directory display NAME; the server resolves who that is. */
  readonly reassignTaskOwner: (
    taskId: string,
    expectedRevision: number,
    ownerName: string,
  ) => Promise<RouteResult>;
  /**
   * ROUTE a Gate DECLINE (BUILD-DEC-116 — the "No" leg). The client sends the object id, exact reviewed
   * revision where applicable, and mandatory human reason (G-055). The owning module records the durable
   * negative decision; the client never mutates approval state itself.
   */
  readonly declineGate: (
    kind: ApprovalGateKind,
    objectId: string,
    reason: string,
    expectedRevision?: number,
  ) => Promise<RouteResult>;
  /** Request changes on the exact Schedule draft revision shown in the approval inbox. */
  readonly requestScheduleChanges: (
    scheduleId: string,
    expectedRevision: number,
    reason: string,
  ) => Promise<RouteResult>;
  /** ROUTE a Schedule-activation intent to `scheduler.activateSchedule` (the single activation Gate). */
  readonly activateSchedule: (scheduleId: string, expectedRevision: number) => Promise<RouteResult>;
  /** ROUTE an immediate-run intent for an ACTIVE Schedule to `scheduler.triggerImmediateRun` (human-only;
   *  the owning module refuses the agent and an inactive Schedule — the surface renders the honest note). */
  readonly runScheduleNow: (scheduleId: string) => Promise<RouteResult>;
  /** ROUTE a remove intent to `scheduler.removeSchedule` (human-only; the owning module refuses the agent
   *  and any routine that has already run — the surface renders the honest note). */
  readonly removeSchedule: (scheduleId: string) => Promise<RouteResult>;
  /** ROUTE a pause intent to `scheduler.pauseSchedule` (human-only; the owning module refuses the agent
   *  and anything not `active` — the surface renders the honest note). */
  readonly pauseSchedule: (scheduleId: string) => Promise<RouteResult>;
  /** ROUTE a resume intent to `scheduler.resumeSchedule` (human-only; the owning module refuses the agent
   *  and anything not `paused`). Pause's inverse — no second Gate, no approval step. */
  readonly resumeSchedule: (scheduleId: string) => Promise<RouteResult>;
  /** ROUTE a restore intent to `scheduler.restoreSchedule` (human-only; the owning module refuses the agent
   *  and anything not `archived`). The routine comes back PAUSED; its archive reason stays on record. */
  readonly restoreSchedule: (scheduleId: string) => Promise<RouteResult>;

  // --- GLOBAL CHAT (N-CHAT-1..4) — the thin transport the chat dock drives ------------------------------
  /** Mint the chat Session id before the first send — the platform owns Session ids (IFC-DASH-016). */
  readonly createChatSession: () => Promise<string>;
  /**
   * SEND a chat message — route it to `agent.spawnAgentRun` (via POST /chat/send) carrying the AUTHENTICATED
   * human as the initiating Actor (resolved server-side). The body carries ONLY the Session id and the
   * human's message text — NO screen identifier, NO context, NO proactive payload (N-CHAT-1 AC-4): the
   * dashboard reasons about nothing and assembles no Context. Returns the spawn outcome the surface renders
   * honestly (ran → its run's transcript; refused → an honest note; queued → preserved, G-160).
   */
  readonly sendChatMessage: (
    sessionId: string,
    message: string,
    requestId: string,
  ) => Promise<ChatSendResult>;
  /**
   * READ a run's transcript (GET /chat/transcript/:runId) — EXACTLY the `agent.getRunTranscript` projection
   * (excludes system-injected Context and internal mechanics, G-158; carries no internal identifier, G-197).
   * The dashboard renders exactly this and adds nothing (N-CHAT-3 AC-1).
   */
  readonly getChatTranscript: (runId: string) => Promise<ChatTranscriptView>;
  /**
   * READ the owner-scoped chat HISTORY (GET /chat/sessions — IFC-AGENT-021, BUILD-DEC-098): the
   * authenticated user's OWN conversations, newest activity first. Server-derived; nothing cached.
   */
  readonly getChatSessions: () => Promise<readonly ChatSessionRow[]>;
  /**
   * READ one full conversation (GET /chat/sessions/:sessionId — IFC-AGENT-022, BUILD-DEC-098): every
   * run's turns + sanitised activity trace in order. Unknown/not-owned is a uniform 404 (surfaced as an
   * honest error state, never fabricated).
   */
  readonly getChatSession: (sessionId: string) => Promise<ChatSessionTranscriptView>;
  /**
   * OPEN the live-preview SSE stream for a run (GET /chat/stream/:runId) — a born-guarded READ (auth +
   * same-initiator scope are enforced server-side BEFORE the first frame; a 401/403 is a plain error, not a
   * stream). The stream carries ONLY guard-cleared SurfacedOutput — never a credential (G-140) or internal
   * identifier (G-197). BEST-EFFORT PREVIEW: the caller ALWAYS reconciles to `getChatTranscript` on end (the
   * snapshot wins). Returns an unsubscribe that aborts the fetch + closes the reader (cleanup on unmount).
   */
  readonly streamChat: (runId: string, handlers: ChatStreamHandlers) => () => void;
  /**
   * INTERRUPT a running run — route to `agent.interruptAgentRun` (via POST /chat/interrupt). Human-only: the
   * agent Actor is refused IN THE OWNING MODULE (G-054); the dashboard exposes this to the human only and
   * surfaces the resulting `interrupted` state from the transcript read — it never sets the state (N-CHAT-2).
   */
  readonly interruptRun: (runId: string) => Promise<RouteResult>;
  /** Continue one inactivity-paused Run. The request body is empty; run id is the only client input. */
  readonly continueRun: (runId: string) => Promise<ChatContinueResult>;

  // --- KNOWLEDGE BATCH (Memory / Files / Team / Settings) reads -----------------------------------------
  /** Read the Memory wiki-browser projection (a tree level + optionally the focused Node with neighbours). */
  readonly getMemoryBrowser: (
    areaPath: string,
    focusNodeId?: string,
  ) => Promise<MemoryBrowserProjection>;
  /** Search every current Memory article by bounded title/path metadata, including collapsed branches. */
  readonly getMemorySearch: (query: string) => Promise<MemorySearchProjection>;
  /** Read the Memory review lists — pending Update Proposals + open Conflicts + Promoted-Layer entries. */
  readonly getMemoryReview: () => Promise<MemoryReviewProjection>;
  /** Read the Files projection — the deny-by-default viewing projections + delivered-report links. */
  readonly getFiles: () => Promise<FilesProjection>;
  /** Read the Team directory — Users + their authority profile (no credential values, G-140). */
  readonly getDirectory: () => Promise<DirectoryProjection>;
  /** Read the Settings projection — the User directory + the value-free config inventory. */
  readonly getSettings: () => Promise<SettingsProjection>;
  /** Read this Deployment's org display for the shell chrome (name + meta line). A SEPARATE read from
   *  `getSettings` on purpose: that projection is value-free by construction (G-173), this one carries a
   *  configured value. The caller always holds a default, so a rejection is a cosmetic fallback, not an
   *  error state. See MIRRORED_CONTRACT below — the server owner is `api/org-display.ts::OrgDisplayView`. */
  readonly getOrgDisplay: () => Promise<OrgDisplayWire>;
  /** Read the exact authenticated human's display-only shell identity. The request carries no user id. */
  readonly getViewerIdentity: () => Promise<ViewerIdentityWire>;
  // MIRRORED_CONTRACT: server owners `app/modules/dashboard/serve/api-routes.ts` task-detail/message/stream
  // rows + `app/modules/dashboard/api/task-thread.ts::TaskDetailView`. The server validates at runtime.
  /** Read one Task-detail thread — brief/approach + the complete shared conversation + live state. */
  readonly getTaskDetail: (taskId: string) => Promise<TaskDetailProjection>;
  /** Append one human update to the Task's ONE server-resolved Session. `requestId` is retry correlation;
   *  the browser sends no Actor, Session, owner, or authority field. */
  readonly sendTaskMessage: (
    taskId: string,
    message: string,
    requestId: string,
  ) => Promise<ChatSendResult>;
  /** Subscribe to one run in the shared Task thread. The server verifies Task/run membership + viewer scope. */
  readonly streamTaskRun: (
    taskId: string,
    runId: string,
    handlers: ChatStreamHandlers,
  ) => () => void;
  /** Read one Message for its inline conversation reader. Undefined for an unknown Message id. */
  readonly getMessage: (messageId: string) => Promise<MessageReaderProjection | undefined>;

  // --- KNOWLEDGE BATCH decision routes (Memory decision → C; directory change → A) ----------------------
  /**
   * ROUTE a Memory DECISION to the owning `memory` (C) Gate — accept/reject a Proposal, or resolve a
   * Conflict. The dashboard NEVER edits Memory and sets no approval field (P-GATE): the client sends only
   * the object id + the decision; the API authenticates the Actor and routes to C, which writes the
   * deciding User. Returns the honest routed/refused result the surface renders.
   */
  readonly routeMemoryDecision: (
    kind: MemoryDecisionKind,
    objectId: string,
    decision: 'accepted' | 'rejected',
  ) => Promise<RouteResult>;
  /**
   * ROUTE a directory-change PROPOSAL to the owning `identity` (A) module (BUILD-DEC-115: proposing is a
   * human action — any active human User; the agent's own propose leg is unchanged). The body carries the
   * change kind + the human-readable summary (+ the subject User for an edit) — a review item, never a
   * mutation; the Operator's credential-enforced resolve stays the only commit. The dashboard sets no field.
   */
  readonly routeDirectoryPropose: (
    changeKind: DirectoryProposalKind,
    summary: string,
    targetUserId?: string,
  ) => Promise<RouteResult>;
  /**
   * ROUTE a directory-proposal RESOLVE (accept/reject) to the owning `identity` (A) commit Gate — the
   * Operator-only credential-enforced leg (IFC-IDENT-010, unchanged). The dashboard sets no field.
   */
  readonly routeDirectoryResolve: (
    proposalId: string,
    decision: 'accepted' | 'rejected',
  ) => Promise<RouteResult>;
  /**
   * COMMIT a contact-detail change directly (BUILD-DEC-127) — add or retire an address on a person.
   * Not a proposal: the owning `identity` legs commit (and refuse anyone without the directory key,
   * and the agent outright). The dashboard sets no field; the server resolves the Actor.
   */
  readonly routeContactAction: (
    intent:
      | {
          readonly action: 'createPerson';
          readonly displayName: string;
          readonly responsibilities: string;
          readonly emailAddresses: readonly string[];
          readonly accessLevel: 'admin_md' | 'operator' | 'member';
        }
      | { readonly action: 'add'; readonly userId: string; readonly address: string }
      | { readonly action: 'retire'; readonly channelIdentityId: string }
      | {
          readonly action: 'editPerson';
          readonly userId: string;
          readonly displayName?: string;
          readonly responsibilities?: string;
          readonly role?: 'admin_md' | 'operator' | 'member';
        },
  ) => Promise<RouteResult>;

  // --- OPERATE BATCH (Monitoring / Connectors / Capability map) reads — Operator role only --------------
  /**
   * Read the Monitoring projection — run/cost traces + the caps shown against them + the sanitised,
   * hashed-identity audit activity + the gate-refusal/failure counts + Connector health. The `admin_md`
   * monitoring SCOPE is a typed boundary enforced in the OWNING read interfaces (agent / kernel-Audit /
   * integrations), NOT UI hiding — the client renders EXACTLY what the API returns for the resolved Actor
   * and widens nothing (N-FDE-2 AC-6, G-180).
   */
  readonly getMonitoring: () => Promise<MonitoringProjection>;
  /**
   * Read ONE run's verbatim transcript — ACTOR-SCOPED. The OWNING agent read refuses an Actor without the
   * cross-user oversight grant; on a refusal the projection carries a `refused` verdict and the surface
   * renders NO transcript (it widens nothing, N-FDE-2 AC-6). The client sends no actor field — the Actor is
   * resolved server-side.
   */
  readonly getRunTranscript: (runId: string) => Promise<RunTranscriptProjection>;
  /** Read the Connectors projection — each Connector's state/health (NEVER a credential value, G-140). */
  readonly getConnectors: () => Promise<ConnectorsProjection>;
  /** Read the Capability-map projection — the generated registry (read-only; derived from code, P-MAPPED). */
  readonly getCapabilityMap: () => Promise<CapabilityMapProjection>;

  // --- OPERATE BATCH re-auth route (error Connector → H Operator Gate) -----------------------------------
  /**
   * ROUTE an Operator re-authorisation intent for an `error` Connector to the owning `integrations` (H)
   * Gate. The client sends ONLY the Connector id — NEVER a credential value (G-140); the freshly re-consented
   * credential is obtained server-side by the owning module's re-consent flow. The agent Actor is refused in
   * H (P-GATE). Returns the honest routed/refused result the surface renders.
   */
  readonly routeConnectorReauthorisation: (connectorId: string) => Promise<RouteResult>;
  /** Register one supported system at `configured`; body is exactly `{ system }`, with no credential/Actor. */
  readonly routeConnectorConnect: (system: ConnectorSystemWire) => Promise<RouteResult>;
  /**
   * ROUTE an Operator connect-BEGIN intent for a `configured` Connector to the owning `integrations` (H)
   * Operator Gate (Story H-1, IFC-INTG-015). On ok carries the per-system authorize URL (single-use
   * anti-forgery state + the public client_id ONLY — never a secret, G-058); the surface navigates the
   * Operator's browser there. A refusal carries a sanitised note (G-197).
   */
  readonly routeConnectorBeginConnect: (connectorId: string) => Promise<BeginConnectRoute>;
  /**
   * ROUTE an Operator RE-CONSENT intent for a `connected` Connector to the owning `integrations` (H) Operator
   * Gate (IFC-INTG-018). Same shape as begin-connect — ONLY the Connector id, never a credential (G-140) — but
   * for a LIVE connection: the owning module retires the credential minted under the old consent before
   * returning the authorize URL, so the surface navigates the Operator's browser to a fresh consent.
   */
  readonly routeConnectorBeginReconnect: (connectorId: string) => Promise<BeginConnectRoute>;
}

// The SSE frame parser + stream reader live in chat-stream.ts (split under the file-size ceiling);
// re-exported so the existing unit tests + consumers keep their import path.
export { dispatchFrame } from './chat-stream.ts';

/** The begin-connect route result — the authorize URL on ok, a sanitised refusal note otherwise. */
export type BeginConnectRoute =
  | { readonly ok: true; readonly authorizeUrl: string }
  | { readonly ok: false; readonly note: string };

/** The chat endpoints (mirror the Stage-1 DASHBOARD_API_ROUTES chat rows). Send/interrupt are POST intents. */
const CHAT_SESSION_PATH = `${API_PREFIX}/chat/session`;
const METRIC_DELETE_PROPOSAL_PATH = `${API_PREFIX}/metrics/delete-proposal`;
const METRIC_COLLECT_NOW_PATH = `${API_PREFIX}/metrics/collect-now`;

/**
 * Plain copy for a collection that SUCCEEDED as a request but collected nothing — the module's own outcome
 * and duplicate-period skip reason, stated in English. The raw snake_case code never renders (G-197), and
 * no new refusal reason is invented here: every branch mirrors one `runCollection` outcome. Without this the
 * skip/failed cases would return HTTP 200 and the surface would fall silent — the exact no-op the control
 * exists to remove.
 */
function collectNowNote(outcome: string, reason: string | undefined): string | null {
  if (outcome === 'completed') return null;
  if (outcome === 'skipped') {
    if (reason === 'authoritative_value_exists_for_period')
      return 'Nothing collected — this period already has a confirmed figure.';
    if (reason === 'proposed_value_exists_for_period')
      return 'Nothing collected — a figure for this period is already waiting for you to confirm.';
    return 'Nothing collected — this period is already covered.';
  }
  return 'The collector ran but brought nothing back, so nothing was recorded.';
}

/**
 * POST the collect-now intent and READ the outcome the API returns. Unlike an ordinary routed intent, a 200
 * here does NOT mean a figure was collected: `runCollection` legitimately settles `skipped` (an already
 * covered period) or `failed` (an empty/failed collector run) without throwing. Those are reported through
 * the same note channel as a refusal so the surface always says what happened and never re-reads a board
 * that did not change; only a `completed` run returns ok and triggers the re-read.
 */
async function postCollectMetricNow(metricId: string): Promise<RouteResult> {
  const response = await fetch(METRIC_COLLECT_NOW_PATH, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ metricId }),
  });
  if (!response.ok) return { ok: false, note: await safeNote(response) };
  const settled = await readCollectNowOutcome(response);
  return settled === null ? { ok: true } : { ok: false, note: settled };
}

/** Read the settled outcome off a 200 response; an unreadable body is reported, never assumed successful. */
async function readCollectNowOutcome(response: Response): Promise<string | null> {
  try {
    const payload = (await response.json()) as {
      readonly outcome?: unknown;
      readonly reason?: unknown;
    };
    return collectNowNote(
      typeof payload.outcome === 'string' ? payload.outcome : '',
      typeof payload.reason === 'string' ? payload.reason : undefined,
    );
  } catch {
    return 'The collection ran, but its outcome could not be read.';
  }
}
const CHAT_SEND_PATH = `${API_PREFIX}/chat/send`;
const CHAT_INTERRUPT_PATH = `${API_PREFIX}/chat/interrupt`;
const chatContinuePath = (runId: string): string =>
  `${API_PREFIX}/chat/runs/${encodeURIComponent(runId)}/continue`;
const chatTranscriptPath = (runId: string): string =>
  `${API_PREFIX}/chat/transcript/${encodeURIComponent(runId)}`;
const chatStreamPath = (runId: string): string =>
  `${API_PREFIX}/chat/stream/${encodeURIComponent(runId)}`;
const CHAT_SESSIONS_PATH = `${API_PREFIX}/chat/sessions`;
const chatSessionPath = (sessionId: string): string =>
  `${API_PREFIX}/chat/sessions/${encodeURIComponent(sessionId)}`;

/** The Task-scoped destructive route (the rest of the Task/Knowledge paths live in know-api.ts). */
const taskDeletePath = (taskId: string): string =>
  `${API_PREFIX}/tasks/${encodeURIComponent(taskId)}/delete`;
/** The Operate-batch read endpoints (mirror the Stage-1 DASHBOARD_API_ROUTES operate rows). */
const MONITORING_PATH = `${API_PREFIX}/monitoring`;
const CONNECTORS_PATH = `${API_PREFIX}/connectors`;
const CAPABILITY_MAP_PATH = `${API_PREFIX}/capability-map`;
const CONNECTOR_REAUTH_PATH = `${API_PREFIX}/connectors/reauthorise`;
const CONNECTOR_CONNECT_PATH = `${API_PREFIX}/connectors/connect`;
const connectorBeginConnectPath = (connectorId: string): string =>
  `${API_PREFIX}/connectors/${encodeURIComponent(connectorId)}/begin-connect`;
const connectorReconnectPath = (connectorId: string): string =>
  `${API_PREFIX}/connectors/${encodeURIComponent(connectorId)}/reconnect`;
const runTranscriptPath = (runId: string): string =>
  `${API_PREFIX}/monitoring/runs/${encodeURIComponent(runId)}/transcript`;

/**
 * POST a chat send to `/chat/send`. The body carries only Session + message + a browser-crypto retry id —
 * no screen, context or actor. On success the API returns the `agent.spawnAgentRun` outcome; a
 * refused send is surfaced with a sanitised note (never an internal error — G-197). The dashboard never
 * retries a Fence-crossing write on its own (§8) — it returns the outcome for the dock to render honestly.
 */
/** POST `/chat/session` — the platform MINTS the Session id the dock then sends under (IFC-DASH-016). */
async function postCreateChatSession(): Promise<string> {
  const response = await fetch(CHAT_SESSION_PATH, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    credentials: 'same-origin',
    body: '{}',
  });
  if (!response.ok) throw new Error('chat session could not be established');
  const body = (await response.json()) as { readonly sessionId: string };
  return body.sessionId;
}

async function postMessage(
  path: string,
  body: Readonly<Record<string, string>>,
): Promise<ChatSendResult> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    return { kind: 'refused', note: await safeNote(response) };
  }
  return (await response.json()) as ChatSendResult;
}

async function postChatSend(
  sessionId: string,
  message: string,
  requestId: string,
): Promise<ChatSendResult> {
  return postMessage(CHAT_SEND_PATH, { sessionId, message, requestId });
}

/** GET a typed projection from a read endpoint. A non-OK response is surfaced honestly (never fabricated). */
async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    method: 'GET',
    headers: { accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) {
    // Fail honestly — the surface renders its error state (P-SELF); no fabricated projection.
    throw new Error(`read failed (${String(response.status)})`);
  }
  return (await response.json()) as T;
}

/** POST a write intent to a Gate route. The body carries ONLY the intent's object id — never an actor. */

/** POST the connect-BEGIN intent; the ok body carries ONLY the authorize URL (never a secret — G-058). */
async function postBeginConnect(path: string): Promise<BeginConnectRoute> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({}),
  });
  if (response.ok) {
    const payload = (await response.json()) as { readonly authorizeUrl?: unknown };
    if (typeof payload.authorizeUrl === 'string' && payload.authorizeUrl.length > 0) {
      return { ok: true, authorizeUrl: payload.authorizeUrl };
    }
    // An ok response with no destination is never navigated blindly — an honest note instead (P-SELF).
    return { ok: false, note: 'The connect flow did not return an authorize destination.' };
  }
  return { ok: false, note: await safeNote(response) };
}

function streamChat(runId: string, handlers: ChatStreamHandlers): () => void {
  return streamRun(chatStreamPath(runId), handlers);
}

/** Continue is not a generic RouteResult: a successful response carries the one successor Run id. */
async function postChatContinue(runId: string): Promise<ChatContinueResult> {
  const response = await fetch(chatContinuePath(runId), {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({}),
  });
  if (!response.ok) return { ok: false, note: await safeNote(response) };
  try {
    const payload = (await response.json()) as {
      readonly kind?: unknown;
      readonly runId?: unknown;
    };
    if (payload.kind === 'started' && isSafeRunId(payload.runId)) {
      return { ok: true, runId: payload.runId };
    }
  } catch {
    // An unreadable success is not treated as a started run; no guessed id and no blind retry.
  }
  return { ok: false, note: 'Ritona could not resume this run.' };
}

function isSafeRunId(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length >= 1 &&
    value.length <= 256 &&
    /^[A-Za-z0-9_-]+$/u.test(value)
  );
}

// The KNOWLEDGE slice (Memory / Files / Team / Settings / Task-detail / Email) lives in know-api.ts
// (split under the file-size ceiling); it is spread into the transport below unchanged.

/** The REAL transport: fetch against the served Stage-1 endpoints. Wired into the app at mount. */
export function httpSurfaceApi(): SurfaceApi {
  return {
    getToday: () => getJson<TodayProjection>(`${API_PREFIX}/today`),
    getTaskBoard: () => getJson<TaskBoardWire>(`${API_PREFIX}/tasks`),
    cancelTask: postTaskCancellation,
    deleteTask: (taskId) => postIntent(taskDeletePath(taskId), {}),
    getMetricsBoard: () => getJson<MetricsBoardWire>(`${API_PREFIX}/metrics`),
    getScheduleHealth: () => getJson<ScheduleHealthProjection>(`${API_PREFIX}/scheduler`),
    getApprovals: () => getJson<ApprovalsInboxProjection>(`${API_PREFIX}/approvals`),
    getMetricEvidence: (valueId) =>
      getJson<MetricEvidenceSheet>(`${API_PREFIX}/metrics/evidence/${encodeURIComponent(valueId)}`),
    deleteMetricProposal: (metricId) => postIntent(METRIC_DELETE_PROPOSAL_PATH, { metricId }),
    collectMetricNow: (metricId) => postCollectMetricNow(metricId),
    ...httpApprovalApi(),
    runScheduleNow: (scheduleId) => postIntent(`${API_PREFIX}/scheduler/run-now`, { scheduleId }),
    removeSchedule: (scheduleId) => postIntent(`${API_PREFIX}/scheduler/remove`, { scheduleId }),
    pauseSchedule: (scheduleId) => postIntent(`${API_PREFIX}/scheduler/pause`, { scheduleId }),
    resumeSchedule: (scheduleId) => postIntent(`${API_PREFIX}/scheduler/resume`, { scheduleId }),
    restoreSchedule: (scheduleId) => postIntent(`${API_PREFIX}/scheduler/restore`, { scheduleId }),
    createChatSession: () => postCreateChatSession(),
    sendChatMessage: (sessionId, message, requestId) => postChatSend(sessionId, message, requestId),
    getChatTranscript: (runId) => getJson<ChatTranscriptView>(chatTranscriptPath(runId)),
    getChatSessions: () => getJson<readonly ChatSessionRow[]>(CHAT_SESSIONS_PATH),
    getChatSession: (sessionId) => getJson<ChatSessionTranscriptView>(chatSessionPath(sessionId)),
    streamChat,
    interruptRun: (runId) => postIntent(CHAT_INTERRUPT_PATH, { runId }),
    continueRun: postChatContinue,

    ...httpKnowledgeApi(),

    // The deployment-brand half of shell chrome: its own narrow endpoint, never `/settings`.
    getOrgDisplay: () => getJson<OrgDisplayWire>(`${API_PREFIX}/org`),
    // Identity is selected exclusively by the server-resolved request Actor; the URL/body carries no id.
    getViewerIdentity: () => getJson<ViewerIdentityWire>(`${API_PREFIX}/viewer`),
    getMonitoring: () => getJson<MonitoringProjection>(MONITORING_PATH),
    getRunTranscript: (runId) => getJson<RunTranscriptProjection>(runTranscriptPath(runId)),
    getConnectors: () => getJson<ConnectorsProjection>(CONNECTORS_PATH),
    getCapabilityMap: () => getJson<CapabilityMapProjection>(CAPABILITY_MAP_PATH),
    routeConnectorReauthorisation: (connectorId) =>
      postIntent(CONNECTOR_REAUTH_PATH, { connectorId }),
    routeConnectorConnect: (system) => postIntent(CONNECTOR_CONNECT_PATH, { system }),
    routeConnectorBeginConnect: (connectorId) =>
      postBeginConnect(connectorBeginConnectPath(connectorId)),
    routeConnectorBeginReconnect: (connectorId) =>
      postBeginConnect(connectorReconnectPath(connectorId)),
    ...httpGovernanceApi(),
  };
}
